import FolderTree from "react-folder-tree";
import "react-folder-tree/dist/style.css";
import "./style.css";
import React, { useEffect, useState } from "react";
import { FileTreeData } from "./types/fileTreeData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import {
  faAngleDown,
  faAngleRight,
  faFileLines,
  faFolder,
  faFolderOpen,
  faFolderPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { getPathname } from "@utils/helperFunctions";
import { useV2CreateFolderMutation } from "@generated";

interface FolderTreeProps {
  fileTreeData: FileTreeData;
  storageOptions: any;
  setFileTreeData: (state) => void;
  rootFolder: string;
  setRootFolder: (state) => void;
  allFolders: any[] | [];
  setAllFolders: (state) => void;
  allFiles: any[] | [];
  setAllFiles: (state) => void;
  setTreeData: (folders, files) => void;
  showCheckbox?: boolean;
  readOnly?: boolean;
  onNameClickPassed?: ({
    defaultOnClick,
    nodeData,
  }: {
    defaultOnClick: any;
    nodeData: any;
  }) => void;
  onChange?: (e: any) => void;
  DeleteIconPassed?: any;
  AddFileIconPassed?: any;
  FileIconPassed?: any;
  CaretRightIconPassed?: any;
  OpenFolderIconPassed?: any;
  FolderIconPassed?: any;
  EditIconPassed?: any;
  CaretDownIconPassed?: any;
  AddFolderIconPassed?: any;
  /** Unused since the v2 migration (v2 is token-scoped); still accepted. */
  account_slug?: string;
  indentPixels?: number;
  /** Unused since the v2 migration (the token comes from the store). */
  authorizationCode?: string;
  selectedConnection?: any;
  selectedIntegration?: any;
  canCreateFolder?: boolean;
  canEditFolderName?: boolean;
  showFileTreeModal?: boolean;
  setShowFileTreeModal?: (boolState) => void;
  setSelectedIntegration?: (state) => void;
  setSelectedConnection?: (state) => void;
  folderLoading?: boolean;
  setFolderLoading?: (boolState) => void;
  selectedFolder?: null | { id: string; name: string };
  setSelectedFolder?: (state) => void;
  setOriginalSelectedFolder?: (state) => void;
}

function FolderTreeComponent({
  showCheckbox,
  readOnly,
  fileTreeData,
  onNameClickPassed,
  onChange,
  DeleteIconPassed,
  AddFileIconPassed,
  FileIconPassed,
  OpenFolderIconPassed,
  CaretRightIconPassed,
  AddFolderIconPassed,
  EditIconPassed,
  FolderIconPassed,
  CaretDownIconPassed,
  indentPixels,
  selectedConnection,
  selectedIntegration,
  showFileTreeModal,
  setShowFileTreeModal,
  setSelectedIntegration,
  setSelectedConnection,
  storageOptions,
  folderLoading,
  setFolderLoading,
  selectedFolder,
  setSelectedFolder,
  setOriginalSelectedFolder,
  canCreateFolder,
  canEditFolderName,
  rootFolder,
  setRootFolder,
  allFolders,
  setAllFolders,
  allFiles,
  setAllFiles,
  setTreeData,
}: FolderTreeProps) {
  const [createFolder] = useV2CreateFolderMutation();

  // There is deliberately no per-folder contents fetch here. The v1 version
  // fetched `get-contents?filter[folder]=` on expand/select but gated the
  // result on `data.length > 0`, and `data` is a single resource object, so the
  // response was never applied; the tree is built from the modal's full
  // contents fetch.

  useEffect(() => {
    setTimeout(function () {
      if (showFileTreeModal) {
        let foundFolder = allFolders?.find((f) => f.id == selectedFolder?.id);
        if (!foundFolder) {
          foundFolder = {
            id: "0",
            name: rootFolder,
            file_url: `/${rootFolder}`,
            type: "folder",
          };
        }
        onNameClick(
          {
            defaultOnClick: () => null,
            nodeData: foundFolder,
          },
          true
        );
      }
    }, 100);
  }, [showFileTreeModal]);

  const onNameClick = (
    { defaultOnClick, nodeData },
    isFromModalStateChange
  ) => {
    defaultOnClick();
    if (onNameClickPassed) {
      onNameClickPassed(  { defaultOnClick, nodeData });
    }

    const { id, file_url, type, name } = nodeData;

    const folderNames = Array.from(
      document.getElementsByClassName("displayName")
    );
    folderNames.forEach((folderElement) => {
      if (
        folderElement.innerHTML.toLocaleLowerCase() == name.toLocaleLowerCase()
      ) {
        folderElement.classList.add("selectedFolder");
      } else {
        folderElement.classList.remove("selectedFolder");
      }
    });
    let newSelectedFolder = {
      id: id,
      name:
        id === "0" || name.toLocaleLowerCase() == rootFolder.toLocaleLowerCase()
          ? rootFolder
          : getPathname(file_url),
    };

    if (newSelectedFolder.id == selectedFolder?.id && !isFromModalStateChange) {
      return;
    }

    if (type === "folder") {
      if (setSelectedFolder) {
        setSelectedFolder(newSelectedFolder);
      }
    } else {
      let folderOfFile = allFolders?.find(
        (folder) => folder?.["id"] == nodeData.folder_id
      );
      newSelectedFolder = {
        id: folderOfFile?.["id"] || "0",
        name: folderOfFile
          ? getPathname(folderOfFile?.["file_url"])
          : rootFolder,
      };
      if (setSelectedFolder) {
        setSelectedFolder(newSelectedFolder);
      }
    }
  };

  const postFolder = (folder) =>
    createFolder({
      id: String(selectedConnection?.["id"]),
      filter: { folder: folder.parent_folder_id },
      body: {
        data: { type: "file-storages", attributes: { name: folder.name } },
      },
    }).unwrap();

  const onTreeStateChange = (e) => {
    e?.children?.forEach((child) => {
      const foldersWithNewFolderName = Array.from(
        new Set(
          e?.children
            ?.filter((f) => f.name.includes("new folder"))
            .concat(allFolders.filter((f) => f.name.includes("new folder")))
        )
      );
      if (!child.id) {
        child = {
          ...child,
          parent_folder_id: e.id,
          name:
            foldersWithNewFolderName.length === 0
              ? child.name
              : child.name + `(${foldersWithNewFolderName.length})`,
        };
        Array.from(document.getElementsByClassName(".displayName")).forEach(
          (el) => {
            if (el.innerHTML == "new folder") {
              el.innerHTML = child.name;
            }
          }
        );
        postFolder(child).then((res) => {
          const created = res?.data?.attributes?.folders;
          const data = (Array.isArray(created) ? created[0] : created) ?? {};
          const newlyAddedFolder = {
            ...data,
            file_url: data.folder_url,
            type: "folder",
            children: [],
          };
          setAllFolders([...allFolders, newlyAddedFolder]);
          onNameClick(
            {
              defaultOnClick: () => null,
              nodeData: newlyAddedFolder,
            },
            false
          );
        });
      }
      if (child?.children?.length > 0) {
        onTreeStateChange(child);
      }
    });
  };

  const DeleteIcon = (...args) => null;
  const AddFileIcon = (...args) => null;
  const CancelIcon = (...args) => null;
  const CaretRightIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = () => {
      defaultOnClick();
    };

    // custom Style
    return <FontAwesomeIcon onClick={handleClick} icon={faAngleRight} />;
  };

  const CaretDownIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = () => {
      defaultOnClick();
    };

    // custom Style
    return <FontAwesomeIcon onClick={handleClick} icon={faAngleDown} />;
  };

  const FileIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = () => {
      defaultOnClick();
    };

    // custom Style
    return <FontAwesomeIcon onClick={handleClick} icon={faFileLines} />;
  };

  const FolderIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = () => {
      defaultOnClick();
    };

    // custom Style
    return <FontAwesomeIcon onClick={handleClick} icon={faFolder} />;
  };

  const OpenFolderIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = () => {
      defaultOnClick();
    };

    // custom Style
    return <FontAwesomeIcon onClick={handleClick} icon={faFolderOpen} />;
  };

  const AddFolderIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    // custom event handler
    const handleClick = (nodeData) => {
      defaultOnClick();
    };

    if (!canCreateFolder) {
      return null;
    } else {
      return (
        <FontAwesomeIcon
          onClick={(e) => handleClick(nodeData)}
          icon={faFolderPlus}
        />
      );
    }
  };

  const EditIcon = ({ onClick: defaultOnClick, nodeData }) => {
    const { path, name, checked, isOpen, ...restData } = nodeData;

    const handleClick = () => {
      defaultOnClick();
    };

    if (!canEditFolderName) {
      return null;
    } else {
      return <FontAwesomeIcon onClick={handleClick} icon={faPencil} />;
    }
  };
  let iconComponents: any = {
    DeleteIcon: DeleteIconPassed ? DeleteIconPassed : DeleteIcon,
    AddFileIcon: AddFileIconPassed ? AddFileIconPassed : AddFileIcon,
    FileIcon: FileIconPassed ? FileIconPassed : FileIcon,
    FolderIcon: FolderIconPassed ? FolderIconPassed : FolderIcon,
    CaretRightIcon: CaretRightIconPassed
      ? CaretRightIconPassed
      : CaretRightIcon,
    CaretDownIcon: CaretDownIconPassed ? CaretDownIconPassed : CaretDownIcon,
    AddFolderIcon: AddFolderIconPassed ? AddFolderIconPassed : AddFolderIcon,
    EditIcon: EditIconPassed ? EditIconPassed : EditIcon,
    OpenFolderIcon: OpenFolderIconPassed
      ? OpenFolderIconPassed
      : OpenFolderIcon,
  };
  if (!canCreateFolder && !canEditFolderName) {
    iconComponents = { ...iconComponents, CancelIcon };
  }
  return (
    <FolderTree
      data={fileTreeData}
      showCheckbox={showCheckbox}
      readOnly={readOnly}
      onNameClick={(e) => {
        onNameClick(
          { defaultOnClick: e.defaultOnClick, nodeData: e.nodeData },
          false
        );
      }}
      onChange={onChange}
      iconComponents={iconComponents}
      indentPixels={indentPixels}
    />
  );
}

export default FolderTreeComponent;
